import {
  ArrowRight,
  Boxes,
  Trash2,
  Clock3,
  Handshake,
  MapPin,
  Menu,
  PackageCheck,
  Phone,
  ShieldCheck,
  Truck,
  Users,
  Warehouse,
  Wrench,
  X,
  Zap,
  ChevronDown,
  CheckCircle2,
} from "lucide-react";
import { useState } from "react";
import { useLang } from "@/i18n/LangContext";
import { langNames, langFlags, type Lang } from "@/i18n/translations";

const LOGO_URL =
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-23/prod_agent_3a0812e8-cabc-4e60-9023-9eb878dbad11/ds_logo_transparent_1343324419e041449c280c0bfa45d1f3.png";
const FLEET_URL =
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-23/prod_agent_3a0812e8-cabc-4e60-9023-9eb878dbad11/IMG_8680_ac5ec955df9b486aba5720831be7c171.jpg";
const VIDEO_URL =
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-23/prod_agent_3a0812e8-cabc-4e60-9023-9eb878dbad11/VIDEO-2026-06-23-10-33-34_8f0cfeac28774b25b1ce105ece222587.mp4";

const LANGS: Lang[] = ["de", "fr", "it", "en", "sq"];

const serviceIcons = [Truck, Wrench, Warehouse, PackageCheck, Boxes, Trash2, Zap, ShieldCheck];
const serviceKeys = [
  { titleKey: "svc_transport", textKey: "svc_transport_text" },
  { titleKey: "svc_montage", textKey: "svc_montage_text", link: "/#/montage" },
  { titleKey: "svc_showroom", textKey: "svc_showroom_text", link: "/#/showroom" },
  { titleKey: "svc_logistik", textKey: "svc_logistik_text", link: "/#/logistik" },
  { titleKey: "svc_zuegel", textKey: "svc_zuegel_text", link: "/#/zuegel" },
  { titleKey: "svc_entsorgung", textKey: "svc_entsorgung_text", link: "/#/entsorgung" },
  { titleKey: "svc_elektro", textKey: "svc_elektro_text", link: "/#/elektro" },
  { titleKey: "svc_flexschutz", textKey: "svc_flexschutz_text", link: "/#/flexschutz" },
];

const statsData = [
  { value: "350+", labelKey: "stat_mitarbeiter", detail: "Ausgebildetes Team für Transport, Montage und Showroom-Betrieb auf höchstem Standard." },
  { value: "150",  labelKey: "stat_fahrzeuge",   detail: "Moderner Fuhrpark für regionale, städtische und Retail-Logistik in der ganzen Schweiz." },
  { value: "250K", labelKey: "stat_kunden",      detail: "Hohes Ausführungsvolumen mit konstantem Servicestandard und nachhaltiger Kundenzufriedenheit." },
  { value: "15+",  labelKey: "stat_partner",     detail: "Kontinuierliche Zusammenarbeit mit Retailern, Showroom-Netzwerken und Distributoren." },
  { value: "58+",  labelKey: "stat_punkte",      detail: "Flächendeckendes Netzwerk in der gesamten Schweiz für schnelle und zuverlässige Lieferungen." },
];

const advantageIcons = [ShieldCheck, Clock3, Handshake];
const advantageKeys = [
  { titleKey: "adv1_title", textKey: "adv1_text" },
  { titleKey: "adv2_title", textKey: "adv2_text" },
  { titleKey: "adv3_title", textKey: "adv3_text" },
];

const processKeys = [
  { num: "01", titleKey: "process1_title", textKey: "process1_text" },
  { num: "02", titleKey: "process2_title", textKey: "process2_text" },
  { num: "03", titleKey: "process3_title", textKey: "process3_text" },
  { num: "04", titleKey: "process4_title", textKey: "process4_text" },
];

const techIcons = [Truck, MapPin, Clock3, ShieldCheck];
const techKeys = [
  { titleKey: "tech_route", textKey: "tech_route_text" },
  { titleKey: "tech_track", textKey: "tech_track_text" },
  { titleKey: "tech_48h",   textKey: "tech_48h_text"   },
  { titleKey: "tech_epod",  textKey: "tech_epod_text"  },
];

const svcFormKeys = [
  "svc_transport",
  "svc_montage",
  "svc_entsorgung", // Küchenmontage maps to entsorgung label isn't ideal but we keep original German for form values
  "svc_showroom",
  "svc_zuegel",
  "svc_entsorgung",
  "svc_logistik",
];

// Fixed German labels for the form checkboxes (form values stay in German for email)
const svcFormLabels = [
  "Möbeltransport",
  "Professionelle Montage",
  "Küchenmontage",
  "Showroom-Service",
  "Zügeln / Umzüge",
  "Entsorgung",
  "Operative Logistik / B2B",
];

const Index = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const { lang, setLang, t } = useLang();
  const [langOpen, setLangOpen] = useState(false);
  const [openService, setOpenService] = useState<string>("svc_transport");

  const navLinks = [
    { id: "services",    label: t("nav_services")   },
    { id: "capacity",    label: t("nav_capacity")   },
    { id: "advantages",  label: t("nav_map")        },
    { id: "technology",  label: t("nav_technology") },
    { id: "contact",     label: t("nav_contact")    },
  ];

  const scrollTo = (id: string) => {
    if (typeof document === "undefined") return;
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* @section: background-glow */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden="true">
        <div className="absolute -left-32 -top-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -right-16 top-96 h-[32rem] w-[32rem] rounded-full bg-primary/7 blur-3xl" />
      </div>

      {/* @section: navbar */}
      <header className="sticky top-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
          {/* Logo */}
          <button
            onClick={() => scrollTo("hero")}
            className="flex items-center gap-3 transition-opacity hover:opacity-85"
          >
            <img src={LOGO_URL} alt="DS Logistik" className="h-14 w-auto" />
            <div className="hidden sm:block leading-tight">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                {t("navbar_since")}
              </p>
              <p className="text-[13px] font-semibold text-foreground/90">
                {t("navbar_tagline")}
              </p>
            </div>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden items-center gap-8 md:flex">
            {navLinks.map(({ label, id }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="text-sm font-medium text-foreground/65 transition-colors hover:text-primary"
              >
                {label}
              </button>
            ))}
          </nav>

          {/* Language Switcher + CTA + Hamburger */}
          <div className="flex items-center gap-3">

            {/* Language Switcher */}
            <div className="relative hidden md:block">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 rounded-full border border-border/60 bg-muted/60 px-3 py-1.5 text-xs font-bold text-foreground/80 backdrop-blur-sm hover:bg-muted transition-colors"
              >
                <span>{langFlags[lang]}</span>
                <span>{langNames[lang]}</span>
                <svg
                  className={`h-3 w-3 transition-transform ${langOpen ? "rotate-180" : ""}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                </svg>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-2 overflow-hidden rounded-2xl border border-border/60 bg-card shadow-xl z-50 min-w-[90px]">
                  {LANGS.map((l) => (
                    <button
                      key={l}
                      onClick={() => { setLang(l); setLangOpen(false); }}
                      className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-sm transition-colors hover:bg-muted ${lang === l ? "bg-primary/10 text-primary font-bold" : "text-foreground/80"}`}
                    >
                      <span>{langFlags[l]}</span>
                      <span>{langNames[l]}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => scrollTo("contact")}
              className="hidden items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-[0_8px_24px_-10px_rgba(200,25,11,0.7)] transition-all duration-200 hover:-translate-y-px hover:shadow-[0_12px_30px_-10px_rgba(200,25,11,0.75)] md:inline-flex"
            >
              {t("nav_cta")} <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="rounded-full border border-border p-2 transition-colors hover:bg-muted md:hidden"
              aria-label="Menü öffnen"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="border-t border-border/60 bg-card px-5 pb-5 pt-4 md:hidden">
            <div className="flex flex-col gap-2">
              {navLinks.map(({ label, id }) => (
                <button
                  key={id}
                  onClick={() => scrollTo(id)}
                  className="rounded-2xl border border-border bg-background px-4 py-3 text-left text-sm font-medium text-foreground/80 hover:text-primary"
                >
                  {label}
                </button>
              ))}

              {/* Mobile language switcher */}
              <div className="mt-2 flex gap-2 flex-wrap">
                {LANGS.map((l) => (
                  <button
                    key={l}
                    onClick={() => { setLang(l); setMenuOpen(false); }}
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${lang === l ? "border-primary bg-primary/10 text-primary" : "border-border bg-background text-foreground/60 hover:border-primary/40"}`}
                  >
                    <span>{langFlags[l]}</span>
                    <span>{langNames[l]}</span>
                  </button>
                ))}
              </div>

              <button
                onClick={() => scrollTo("contact")}
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
              >
                {t("nav_cta")} <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </header>

      <main>
        {/* @section: hero */}
        <section id="hero" className="relative min-h-[90vh] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={FLEET_URL}
              alt="DS Logistik Fuhrpark vor dem Lager"
              className="h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/38 to-black/10" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
          </div>

          <div className="relative mx-auto flex max-w-7xl flex-col justify-end px-5 pb-16 pt-24 lg:min-h-[90vh] lg:px-8 lg:pb-24">

            {/* Hero Logo — full width, edge to edge, dominant */}
            <div className="mb-12 w-full rounded-3xl bg-white/12 px-10 py-14 backdrop-blur-md ring-2 ring-white/25 sm:px-16 sm:py-20">
              <img
                src={LOGO_URL}
                alt="DS Logistik Schweiz"
                className="mx-auto w-full max-w-none object-contain drop-shadow-[0_12px_60px_rgba(220,30,30,0.85)]"
                style={{ maxHeight: "320px" }}
              />
            </div>

            <div className="grid gap-12 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
              {/* Left: Copy */}
              <div>
                <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-white/80 backdrop-blur-sm">
                  <Truck className="h-3.5 w-3.5 text-primary" />
                  {t("hero_badge")}
                </div>

                <h1 className="max-w-3xl text-4xl font-bold leading-[0.92] text-white sm:text-5xl lg:text-[4.5rem]">
                  {t("hero_title1")}{" "}
                  <span className="text-primary">{t("hero_title2")}</span>
                  {t("hero_title3")}
                </h1>

                <p className="mt-6 max-w-xl text-base leading-7 text-white/72 sm:text-lg">
                  {t("hero_desc")}
                </p>

                <div className="mt-8 flex flex-wrap gap-4">
                  <button
                    onClick={() => scrollTo("contact")}
                    className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-4 text-sm font-bold text-primary-foreground shadow-[0_16px_40px_-16px_rgba(200,25,11,0.85)] transition-all duration-200 hover:-translate-y-0.5"
                  >
                    {t("hero_btn_contact")} <ArrowRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => scrollTo("services")}
                    className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-7 py-4 text-sm font-semibold text-white backdrop-blur-sm transition-all duration-200 hover:bg-white/16"
                  >
                    {t("hero_btn_services")}
                  </button>
                </div>
              </div>

              {/* Right: Floating stats card */}
              <div className="rounded-3xl border border-white/14 bg-black/45 p-6 backdrop-blur-md lg:ml-auto lg:w-80">
                <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.32em] text-white/50">
                  {t("stats_label")}
                </p>
                <div className="space-y-3.5">
                  {[
                    { n: "350+", lk: "stat_mitarbeiter" },
                    { n: "150",  lk: "stat_fahrzeuge"   },
                    { n: "250K", lk: "stat_kunden"      },
                    { n: "15+",  lk: "stat_partner"     },
                    { n: "58+",  lk: "stat_punkte"      },
                  ].map(({ n, lk }) => (
                    <div
                      key={lk}
                      className="flex items-baseline gap-3 border-b border-white/8 pb-3.5 last:border-0 last:pb-0"
                    >
                      <span className="font-mono text-3xl font-bold text-white">{n}</span>
                      <span className="text-sm text-white/60">{t(lk)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* @section: video-showreel */}
        <section id="video" className="bg-foreground py-0">
          <div className="relative w-full overflow-hidden" style={{ maxHeight: "420px" }}>
            <video
              src={VIDEO_URL}
              autoPlay
              muted
              loop
              playsInline
              className="w-full object-cover"
              style={{ maxHeight: "420px" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 px-6 py-8 sm:px-12">
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/60">{t("video_badge")}</p>
              <h2 className="mt-2 text-2xl font-bold text-white sm:text-3xl">{t("video_title")}</h2>
            </div>
          </div>
        </section>

        {/* @section: partners */}
        <section className="border-b border-border/70 bg-muted/30 py-12">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <p className="mb-8 text-center font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
              Vertrauenspartner — Wir arbeiten mit den führenden Möbelhändlern der Schweiz
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-10">
              {[
                { name: "Conforama",  logo: "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-24/prod_agent_3a0812e8-cabc-4e60-9023-9eb878dbad11/conforama_1_890cdd647927493a931c156a39e7ba30.png", href: "https://www.conforama.ch" },
                { name: "XXXLutz",   logo: "https://upload.wikimedia.org/wikipedia/commons/f/f1/XXXLutz_Logo.svg", href: "https://www.xxxlutz.ch" },
                { name: "mömax",     logo: "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-24/prod_agent_3a0812e8-cabc-4e60-9023-9eb878dbad11/momax_2_d700a1f89e544a9584d924af971d1648.png", href: "https://www.momax.ch" },
                { name: "Lipo",      logo: "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-24/prod_agent_3a0812e8-cabc-4e60-9023-9eb878dbad11/lipo_1_26675db61fae45a7bf7f7660228846f8.png", href: "https://www.lipo.ch" },
                { name: "Lumimart",  logo: "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-24/prod_agent_3a0812e8-cabc-4e60-9023-9eb878dbad11/lumimart_1_5ab4efd82bb442a6a21748768fb96f31.png", href: "https://www.lumimart.ch" },
                { name: "Molino 48", logo: "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-24/prod_agent_3a0812e8-cabc-4e60-9023-9eb878dbad11/molino48_863b2d97ff4e40a49ab99992c7688c72.svg", href: "https://www.molino48.it" },
                { name: "Nolte",     logo: "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-24/prod_agent_3a0812e8-cabc-4e60-9023-9eb878dbad11/nolte_1_af7d90b1ad894887a38e00273b6ad5c8.png", href: "https://www.nolte.de" },
                { name: "Nobilia",   logo: "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-24/prod_agent_3a0812e8-cabc-4e60-9023-9eb878dbad11/nobilia_1_6c8f289fa13f4caca9f2983826423d9a.png", href: "https://www.nobilia.de" },
              ].map(({ name, logo, href }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={name}
                  className="flex h-16 w-40 items-center justify-center overflow-hidden rounded-xl border border-border/40 bg-white px-4 py-2 shadow-sm transition-all duration-200 hover:shadow-lg hover:scale-105 hover:border-primary/30"
                >
                  <img
                    src={logo}
                    alt={name}
                    className="max-h-10 w-full object-contain"
                    onError={(e) => {
                      const t = e.currentTarget;
                      t.style.display = "none";
                      const span = document.createElement("span");
                      span.className = "text-xs font-bold text-foreground";
                      span.textContent = name;
                      t.parentElement?.appendChild(span);
                    }}
                  />
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* @section: stats-banner */}
        <section id="capacity" className="border-b border-border/70 bg-muted/30">
          <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
            <div className="mb-10 grid gap-4 lg:grid-cols-2 lg:items-end">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">
                  {t("stats_badge")}
                </p>
                <h2 className="mt-3 text-3xl font-bold leading-tight text-foreground sm:text-4xl lg:text-5xl">
                  {t("stats_title")}
                </h2>
              </div>
              <p className="max-w-2xl text-base leading-7 text-muted-foreground lg:text-lg">
                {t("stats_subtitle")}
              </p>
            </div>

            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {statsData.map((s, i) => (
                <article
                  key={s.labelKey}
                  style={{ boxShadow: "0 20px 50px -28px rgba(37,10,8,0.22)" }}
                  className={`rounded-3xl border border-border/60 bg-card p-7 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 ${i === 1 ? "xl:-translate-y-5" : ""}`}
                >
                  <p className="font-mono text-4xl font-bold text-primary">{s.value}</p>
                  <h3 className="mt-4 text-base font-semibold text-foreground">{t(s.labelKey)}</h3>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">{s.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* @section: swiss-map */}
        <section id="map" className="border-b border-border/70">
          <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
            <div className="mb-10 grid gap-4 lg:grid-cols-2 lg:items-end">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">
                  {t("map_badge")}
                </p>
                <h2 className="mt-3 text-3xl font-bold leading-tight text-foreground sm:text-4xl lg:text-5xl">
                  {t("map_title")}
                </h2>
              </div>
              <p className="max-w-2xl text-base leading-7 text-muted-foreground lg:text-lg">
                {t("map_desc")}
              </p>
            </div>

            <div
              style={{ boxShadow: "0 24px 60px -32px rgba(37,10,8,0.28)" }}
              className="overflow-hidden rounded-3xl border border-border/60 bg-card"
            >

              <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border/60 px-6 py-4 sm:px-10">
                <div className="flex items-center gap-2.5">
                  <div className="h-3.5 w-3.5 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: "oklch(0.48 0.22 12)" }} />
                  <span className="text-sm text-muted-foreground">{t("map_legend")}</span>
                </div>
                <div className="flex items-center gap-8">
                  {[
                    ["58+", t("map_standorte")],
                    ["26",  t("map_kantone")],
                    ["48h", t("map_lieferzeit")],
                  ].map(([n, l]) => (
                    <div key={l} className="text-center">
                      <p className="font-mono text-2xl font-bold text-primary">{n}</p>
                      <p className="text-xs text-muted-foreground">{l}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* @section: services */}
        <section id="services">
          <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
            <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
              {/* Left col: intro + fleet img — sticky */}
              <div className="lg:sticky lg:top-28">
                <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">
                  {t("services_badge")}
                </p>
                <h2 className="mt-3 text-3xl font-bold leading-tight text-foreground sm:text-4xl lg:text-5xl">
                  {t("services_title")}
                </h2>
                <p className="mt-5 text-base leading-7 text-muted-foreground">
                  {t("services_desc")}
                </p>
                <div
                  style={{ boxShadow: "0 20px 50px -24px rgba(37,10,8,0.28)" }}
                  className="mt-8 overflow-hidden rounded-3xl border border-border"
                >
                  <img
                    src={FLEET_URL}
                    alt="DS Logistik Fuhrpark"
                    className="h-64 w-full object-cover"
                  />
                </div>
                <div className="mt-5 flex items-start gap-3 rounded-3xl border border-primary/20 bg-primary/5 p-5">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                  <div>
                    <p className="text-sm font-semibold text-foreground">{t("services_coverage")}</p>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">{t("services_coverage_text")}</p>
                  </div>
                </div>
              </div>

              {/* Right col: ACCORDION */}
              <div className="divide-y divide-border/60 rounded-3xl border border-border/60 bg-card overflow-hidden"
                style={{ boxShadow: "0 20px 50px -24px rgba(37,10,8,0.18)" }}>
                {serviceKeys.map((srv, i) => {
                  const Icon = serviceIcons[i];
                  const isOpen = openService === srv.titleKey;
                  const isMontage = srv.titleKey === "svc_montage";
                  const isEntsorgung = srv.titleKey === "svc_entsorgung";
                  return (
                    <div key={srv.titleKey}>
                      {/* Accordion header — always visible */}
                      <button
                        onClick={() => setOpenService(isOpen ? "" : srv.titleKey)}
                        className={`w-full flex items-center gap-4 px-6 py-5 text-left transition-colors duration-200 ${
                          isOpen ? "bg-primary/5" : "hover:bg-muted/40"
                        }`}
                      >
                        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors duration-200 ${
                          isOpen ? "bg-primary text-white" : "bg-primary/10 text-primary"
                        }`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <span className={`flex-1 text-base font-semibold ${isOpen ? "text-primary" : "text-foreground"}`}>
                          {t(srv.titleKey)}
                        </span>
                        <ChevronDown className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-300 ${isOpen ? "rotate-180 text-primary" : ""}`} />
                      </button>

                      {/* Accordion body — visible only when open */}
                      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"}`}>
                        <div className={`px-6 pb-6 pt-2 border-l-4 border-primary ml-6`}>
                          <p className="text-sm leading-6 text-muted-foreground">{t(srv.textKey)}</p>

                          {/* Montage pricing */}
                          {isMontage && (
                            <div className="mt-4 space-y-2 border-t border-border/40 pt-4">
                              {[
                                ["1 Stunde", "CHF 199"],
                                ["2 Stunden", "CHF 349"],
                                ["½ Tag (4h)", "CHF 399"],
                                ["Ganzer Tag (8h)", "CHF 699"],
                              ].map(([label, price]) => (
                                <div key={label} className="flex items-center justify-between text-sm">
                                  <span className="text-muted-foreground">{label}</span>
                                  <span className="font-mono font-bold text-primary">{price}</span>
                                </div>
                              ))}
                              <p className="pt-1 text-[11px] text-muted-foreground/70">Inkl. An-/Abfahrt & Verpackungsentsorgung</p>
                            </div>
                          )}

                          {/* Entsorgung pricing */}
                          {isEntsorgung && (
                            <div className="mt-4 space-y-2 border-t border-border/40 pt-4">
                              {[
                                ["Küchenrückbau & Entsorgung", "CHF 1'400/LKW"],
                                ["Altmöbel Entsorgung", "CHF 899/LKW"],
                                ["Express-Entsorgung", "auf Anfrage"],
                              ].map(([label, price]) => (
                                <div key={label} className="flex items-center justify-between gap-2 text-sm">
                                  <span className="text-muted-foreground">{label}</span>
                                  <span className="font-mono font-bold text-primary whitespace-nowrap">{price}</span>
                                </div>
                              ))}
                              <p className="pt-1 text-[11px] text-muted-foreground/70">Inkl. Demontage, Verpackung & fachgerechte Entsorgung — alles aus einer Hand</p>
                            </div>
                          )}

                          {/* Link button if page exists */}
                          {srv.link && (
                            <a
                              href={srv.link}
                              className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-primary/90 transition-colors"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                              Mehr erfahren →
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* @section: advantages */}
        <section id="advantages" className="border-t border-border/70 bg-muted/25">
          <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
            <div className="grid gap-12 lg:grid-cols-2">
              {/* Left: advantages */}
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">
                  {t("adv_badge")}
                </p>
                <h2 className="mt-3 text-3xl font-bold leading-tight text-foreground sm:text-4xl lg:text-5xl">
                  {t("adv_title")}
                </h2>
                <div className="mt-10 space-y-5">
                  {advantageKeys.map((adv, i) => {
                    const Icon = advantageIcons[i];
                    return (
                      <div
                        key={adv.titleKey}
                        style={{ boxShadow: "0 12px 30px -18px rgba(37,10,8,0.18)" }}
                        className="flex gap-4 rounded-3xl border border-border/60 bg-card p-6"
                      >
                        <div
                          style={{ boxShadow: "0 8px 24px -10px rgba(200,25,11,0.65)" }}
                          className="mt-0.5 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground"
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <h3 className="text-base font-semibold text-foreground">{t(adv.titleKey)}</h3>
                          <p className="mt-2 text-sm leading-6 text-muted-foreground">{t(adv.textKey)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right: process + partner card */}
              <div className="flex flex-col gap-5">
                {/* Process */}
                <div
                  style={{ boxShadow: "0 20px 50px -28px rgba(37,10,8,0.22)" }}
                  className="rounded-3xl border border-border/60 bg-card p-7"
                >
                  <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">
                    {t("process_badge")}
                  </p>
                  <div className="mt-6 space-y-4">
                    {processKeys.map((step) => (
                      <div
                        key={step.num}
                        className="flex items-start gap-4 rounded-2xl border border-border/70 bg-background p-4"
                      >
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 font-mono text-sm font-bold text-primary">
                          {step.num}
                        </span>
                        <div>
                          <p className="text-sm font-semibold text-foreground">{t(step.titleKey)}</p>
                          <p className="mt-1 text-xs leading-5 text-muted-foreground">{t(step.textKey)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Partner fit card */}
                <div
                  style={{ boxShadow: "0 20px 50px -28px rgba(0,0,0,0.6)" }}
                  className="rounded-3xl bg-foreground p-7 text-white"
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">
                    {t("partner_badge")}
                  </p>
                  <h3 className="mt-3 text-2xl font-bold text-white leading-tight">
                    {t("partner_title")}
                  </h3>
                  <div className="mt-6 flex flex-wrap gap-2.5">
                    {[
                      "Möbelhäuser",
                      "Küchenstudios",
                      "Showroom Operators",
                      "Retail Chains",
                      "Property Fit-Out",
                    ].map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-white/15 bg-white/8 px-4 py-1.5 text-sm text-white/75"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* @section: urbantz-tech */}
        <section id="technology" className="border-b border-border/70 bg-card/40">
          <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
            <div className="flex flex-col items-center gap-8 lg:flex-row lg:items-center lg:gap-16">
              <div className="lg:w-1/3 text-center lg:text-left">
                <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">
                  {t("tech_badge")}
                </p>
                <h2 className="mt-3 text-2xl font-bold text-foreground sm:text-3xl">
                  {t("tech_title")}
                </h2>
                <p className="mt-4 text-base leading-7 text-muted-foreground">
                  {t("tech_desc")}
                </p>
                <a
                  href="https://urbantz.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/8 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/15 transition-colors"
                >
                  urbantz.com <ArrowRight className="h-3.5 w-3.5" />
                </a>
              </div>
              <div className="grid grid-cols-2 gap-4 lg:w-2/3 sm:grid-cols-4">
                {techKeys.map(({ titleKey, textKey }, i) => {
                  const Icon = techIcons[i];
                  return (
                    <div key={titleKey} className="rounded-2xl border border-border/60 bg-background p-4">
                      <Icon className="mb-3 h-5 w-5 text-primary" />
                      <p className="text-sm font-semibold text-foreground">{t(titleKey)}</p>
                      <p className="mt-1 text-xs leading-5 text-muted-foreground">{t(textKey)}</p>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Additional tech tools */}
            <div className="mt-8 border-t border-border/50 pt-8">
              <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
                Weitere Systeme &amp; Tools
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  { name: "Bexio", desc: "ERP & Buchhaltung", color: "#00A86B" },
                  { name: "SPIM", desc: "Lagerplanung", color: "#2563EB" },
                  { name: "DKV System", desc: "Fahrzeug & Tankkarte", color: "#003399" },
                  { name: "Urbantz", desc: "Last-Mile Routing", color: "#FF4500" },
                  { name: "Microsoft 365", desc: "Kommunikation", color: "#0078D4" },
                  { name: "Google Workspace", desc: "Collaboration", color: "#4285F4" },
                ].map(({ name, desc, color }) => (
                  <div key={name} className="flex items-center gap-2.5 rounded-full border border-border/60 bg-background px-4 py-2">
                    <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
                    <div>
                      <span className="text-sm font-semibold text-foreground">{name}</span>
                      <span className="ml-1.5 text-xs text-muted-foreground">{desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* @section: contact-cta */}
        <section id="contact" className="border-t border-border/70">
          <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
            <div className="grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">
                  {t("contact_badge")}
                </p>
                <h2 className="mt-3 text-3xl font-bold leading-tight text-foreground sm:text-4xl lg:text-5xl">
                  {t("contact_title")}
                </h2>
                <p className="mt-5 max-w-xl text-base leading-7 text-muted-foreground">
                  {t("contact_desc")}
                </p>
                <a
                  href="mailto:andre.stalder@ds-logistik.ch"
                  className="mt-4 inline-flex items-center gap-2 text-primary font-semibold hover:underline"
                >
                  andre.stalder@ds-logistik.ch <ArrowRight className="h-4 w-4" />
                </a>
              </div>

              <div
                style={{ boxShadow: "0 20px 50px -28px rgba(37,10,8,0.22)" }}
                className="rounded-3xl border border-border/60 bg-card p-7"
              >
                <div className="space-y-4">
                  {[
                    {
                      icon: Phone,
                      titleKey: "contact_phone_label",
                      text: "032 671 20 10",
                    },
                    {
                      icon: MapPin,
                      titleKey: "contact_addr_label",
                      text: "Bielstrasse 20, 4500 Solothurn, Schweiz",
                    },
                    {
                      icon: Users,
                      titleKey: "contact_b2b_label",
                      textKey: "contact_b2b_text",
                    },
                    {
                      icon: Users,
                      titleKey: "contact_b2c_label",
                      textKey: "contact_b2c_text",
                    },
                  ].map(({ icon: Icon, titleKey, text, textKey }) => (
                    <div
                      key={titleKey}
                      className="flex gap-4 rounded-2xl border border-border/70 bg-background p-4"
                    >
                      <Icon className="mt-1 h-5 w-5 shrink-0 text-primary" />
                      <div>
                        <p className="text-sm font-semibold text-foreground">{t(titleKey)}</p>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                          {textKey ? t(textKey) : text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Contact form with service checkboxes */}
                {(() => {
                  return (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const fd = new FormData(e.currentTarget);
                        const name = fd.get("name") as string;
                        const email = fd.get("email") as string;
                        const phone = fd.get("phone") as string;
                        const message = fd.get("message") as string;
                        const selected = svcFormLabels.filter((s) => fd.get(s) === "on");
                        const servicesStr = selected.length ? selected.join(", ") : t("form_no_selection");
                        const requests = JSON.parse(localStorage.getItem("dslogistik_requests") || "[]");
                        requests.push({ id: Date.now(), date: new Date().toISOString(), name, email, phone, services: selected, message, status: "Neu" });
                        localStorage.setItem("dslogistik_requests", JSON.stringify(requests));
                        const subject = encodeURIComponent(`${t("form_subject_prefix")} ${servicesStr} — ${name}`);
                        const body = encodeURIComponent(`${t("form_body_name")}: ${name}\n${t("form_body_email")}: ${email}\n${t("form_body_phone")}: ${phone}\n${t("form_body_services")}: ${servicesStr}\n\n${t("form_body_message")}:\n${message}\n\n---\n${t("form_body_sent")}`);
                        window.location.href = `mailto:andre.stalder@ds-logistik.ch?subject=${subject}&body=${body}`;
                      }}
                      className="mt-6 space-y-4"
                    >
                      <div>
                        <p className="mb-2 text-sm font-semibold text-foreground">{t("form_services_label")}</p>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {svcFormLabels.map((s) => (
                            <label
                              key={s}
                              className="flex cursor-pointer items-center gap-3 rounded-xl border border-border/70 bg-background px-3 py-2.5 text-sm transition-colors hover:border-primary/50 hover:bg-primary/5 has-[:checked]:border-primary has-[:checked]:bg-primary/10"
                            >
                              <input type="checkbox" name={s} className="h-4 w-4 accent-primary" />
                              <span className="text-foreground">{s}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <input
                          name="name"
                          required
                          placeholder={t("form_name")}
                          className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                        <input
                          name="email"
                          type="email"
                          required
                          placeholder={t("form_email")}
                          className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40"
                        />
                      </div>
                      <input
                        name="phone"
                        type="tel"
                        placeholder={t("form_phone")}
                        className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                      <textarea
                        name="message"
                        rows={3}
                        placeholder={t("form_message")}
                        className="w-full rounded-xl border border-border/70 bg-background px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                      />
                      <button
                        type="submit"
                        className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-bold text-primary-foreground shadow-[0_8px_24px_-10px_rgba(200,25,11,0.7)] transition-all duration-200 hover:-translate-y-px"
                      >
                        {t("form_submit")} <ArrowRight className="h-4 w-4" />
                      </button>
                    </form>
                  );
                })()}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* @section: footer */}
      <footer className="border-t border-border/60 bg-foreground">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-5 py-8 text-center sm:flex-row sm:text-left lg:px-8">
          <img src={LOGO_URL} alt="DS Logistik" className="h-14 w-auto brightness-0 invert" />
          <p className="text-sm text-white/45">
            © {new Date().getFullYear()} DS Logistik · {t("footer_copy")}
          </p>
          <div className="flex items-center gap-4">
            <div className="flex flex-wrap gap-4 text-xs">
              <a href="/#/ueber-uns" className="text-white/45 hover:text-white/80 transition-colors">Über uns</a>
              <a href="/#/montage" className="text-white/45 hover:text-white/80 transition-colors">Montage-Service</a>
              <a href="/#/impressum" className="text-white/45 hover:text-white/80 transition-colors">Impressum</a>
              <a href="/#/impressum" className="text-white/45 hover:text-white/80 transition-colors">Datenschutz</a>
            </div>
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/35">
              {t("footer_since")}
            </p>
            <a href="/#/admin" className="font-mono text-[10px] text-white/15 hover:text-white/40 transition-colors">Admin</a>
          </div>
        </div>
      </footer>

      {/* WhatsApp floating button */}
      <a
        href="https://wa.me/41798883262"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-white shadow-[0_4px_20px_rgba(37,211,102,0.5)] transition-all duration-200 hover:scale-105 hover:shadow-[0_6px_28px_rgba(37,211,102,0.65)]"
        aria-label="WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="h-6 w-6 fill-white" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="text-sm font-bold">WhatsApp</span>
      </a>
    </div>
  );
};

export default Index;
